import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { addSeconds, format, isAfter, startOfDay, subDays } from 'date-fns';

import { DatePicker } from '@adeccoux/tag-ds';
import { toLocalDate, tzOffset } from '@helpers/utils';
import { Box } from '@mui/material';
import { RootState } from '@store/rootReducer';

interface AdminAppointmentPickerProps {
  dateFormat?: string;
  onboardingCreatedAtInterval: {
    onboardingCreatedAtEndDate: any;
    onboardingCreatedAtStartDate: any;
  };
  setOnboardingCreatedAtInterval: any;
  useLocalTimeZone?: boolean;
}

const AdminAppointmentPicker: React.FC<AdminAppointmentPickerProps> = ({
  dateFormat,
  onboardingCreatedAtInterval,
  setOnboardingCreatedAtInterval,
  useLocalTimeZone = false,
}) => {
  const { t } = useTranslation(['recruiter']);

  const appointmentStartOffset = useSelector(
    (state: RootState) => state.featureConfiguration.appSettings.appointmentStartOffset,
  );

  const defaultDateFormat = 'yyyy-MM-dd';

  const startDateValue = onboardingCreatedAtInterval.onboardingCreatedAtStartDate
    ? toLocalDate(onboardingCreatedAtInterval.onboardingCreatedAtStartDate, useLocalTimeZone)
    : null;

  const endDateValue = onboardingCreatedAtInterval.onboardingCreatedAtEndDate
    ? toLocalDate(onboardingCreatedAtInterval.onboardingCreatedAtEndDate, useLocalTimeZone)
    : null;

  const addToUrl = (key: string, value: string | null) => {
    const currentSearchParams = new URLSearchParams(window.location.search);
    if (value === null) {
      currentSearchParams.delete(key);
    } else {
      currentSearchParams.set(key, value);
    }

    const newUrl = [window.location.pathname, currentSearchParams.toString()].filter(Boolean).join('?');
    window.history.replaceState(null, '', newUrl);
  };

  const getMaxDateStartDate = () => {
    if (!onboardingCreatedAtInterval.onboardingCreatedAtEndDate) return undefined;

    const endDate = new Date(onboardingCreatedAtInterval.onboardingCreatedAtEndDate);
    const maxDate = subDays(endDate, 1);
    return isAfter(maxDate, new Date()) ? Date.now() : maxDate.getTime();
  };

  const getStartDateDefaultValue = () => {
    const offsetDays = appointmentStartOffset ?? 14;
    const defaultStart = startOfDay(subDays(new Date(), offsetDays));
    return useLocalTimeZone ? addSeconds(defaultStart, -tzOffset).getTime() : defaultStart.getTime();
  };

  return (
    <div className="tag-ds">
      <Box display="flex">
        <div className="onboarding-created-at-interval">
          <DatePicker
            dateFormat={dateFormat || defaultDateFormat}
            name="datepicker"
            placeholder="dd/mm/yyyy"
            onChange={(date: any) => {
              const onboardingCreatedAtStartDate = date ? format(date, defaultDateFormat) : null;
              setOnboardingCreatedAtInterval({
                ...onboardingCreatedAtInterval,
                onboardingCreatedAtStartDate,
              });
              addToUrl('onboardingStartDate', onboardingCreatedAtStartDate);
            }}
            maxDate={getMaxDateStartDate()}
            label={t('GENERAL.CANDIDATES_OVERVIEW.appointmentStart')}
            defaultValue={startDateValue ? startDateValue.getTime() : getStartDateDefaultValue()}
          />
        </div>
        <div className="onboarding-created-at-interval">
          <DatePicker
            dateFormat={dateFormat || defaultDateFormat}
            name="datepicker"
            placeholder="dd/mm/yyyy"
            label={t('GENERAL.CANDIDATES_OVERVIEW.appointmentEnd')}
            minDate={new Date(onboardingCreatedAtInterval.onboardingCreatedAtStartDate).getTime()}
            onChange={(date: any) => {
              const onboardingCreatedAtEndDate = date ? format(date, defaultDateFormat) : null;
              setOnboardingCreatedAtInterval({
                ...onboardingCreatedAtInterval,
                onboardingCreatedAtEndDate,
              });
              addToUrl('onboardingEndDate', onboardingCreatedAtEndDate);
            }}
            defaultValue={endDateValue ? endDateValue.getTime() : Date.now()}
          />
        </div>
      </Box>
    </div>
  );
};

export default AdminAppointmentPicker;
